import React, { useEffect, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { fetchQuizQuestions, submitQuiz } from "../lib/api";
import { ArrowRight, ArrowLeft, Loader2, Sparkles, RefreshCcw } from "lucide-react";
import { Link } from "react-router-dom";

const SCALE_LABELS = (t) => [t("quiz.scale_low"), "", "", "", t("quiz.scale_high")];

const Quiz = () => {
  const { t, lang } = useLang();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // {qid: score}
  const [step, setStep] = useState(-1); // -1 = intro, 0..n-1 = q, n = email, n+1 = result
  const [email, setEmail] = useState("");
  const [first, setFirst] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetchQuizQuestions(lang).then((r) => setQuestions(r.questions));
  }, [lang]);

  const total = questions.length;
  const labels = SCALE_LABELS(t);

  const choose = (qid, score) => {
    setAnswers({ ...answers, [qid]: score });
    setTimeout(() => {
      setStep((s) => Math.min(s + 1, total));
    }, 220);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const payload = {
        email,
        first_name: first,
        language: lang,
        newsletter_opt_in: newsletterOptIn,
        answers: Object.entries(answers).map(([question_id, score]) => ({ question_id, score })),
      };
      const r = await submitQuiz(payload);
      setResult(r);
      setStep(total + 1);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const restart = () => { setAnswers({}); setEmail(""); setFirst(""); setResult(null); setStep(-1); };

  const progressPct = step >= 0 && step < total ? Math.round(((step) / total) * 100) : (step >= total ? 100 : 0);

  return (
    <div className="pt-28 pb-32 min-h-screen" data-testid="quiz-page">
      <div className="max-w-3xl mx-auto px-6">
        <div className="overline mb-4">SAT · Test</div>
        <h1 className="headline-xl text-4xl md:text-6xl mb-6" data-testid="quiz-title">{t("quiz.title")}</h1>
        <p className="text-white/65 mb-10">{t("quiz.subtitle")}</p>

        {/* progress */}
        {step > -1 && step < total + 1 && (
          <div className="mb-10" data-testid="quiz-progress">
            <div className="flex justify-between text-xs uppercase tracking-widest text-white/45 mb-2">
              <span>{step < total ? `${t("quiz.progress")} ${step + 1} ${t("quiz.of")} ${total}` : t("quiz.email_step_title")}</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-px bg-white/10 relative overflow-hidden">
              <div className="absolute left-0 top-0 h-px bg-[#D4AF37] transition-all" style={{ width: `${progressPct}%` }}></div>
            </div>
          </div>
        )}

        {step === -1 && (
          <div className="glass rounded-2xl p-8 md:p-12" data-testid="quiz-intro">
            <div className="serif italic text-2xl text-white/80 mb-8">„{t("hero.whisper")}"</div>
            <ul className="space-y-3 text-white/70 mb-8">
              <li className="flex gap-3"><Sparkles size={16} className="text-[#D4AF37] mt-1" /> 12 {t("quiz.progress")}s · 3 min</li>
              <li className="flex gap-3"><Sparkles size={16} className="text-[#D4AF37] mt-1" /> Persönliche Auswertung per E-Mail</li>
              <li className="flex gap-3"><Sparkles size={16} className="text-[#D4AF37] mt-1" /> 100% anonym & kostenlos</li>
            </ul>
            <button onClick={() => setStep(0)} className="btn-primary" data-testid="quiz-start">{t("quiz.start")} <ArrowRight size={18} /></button>
          </div>
        )}

        {step >= 0 && step < total && questions[step] && (
          <div className="glass rounded-2xl p-8 md:p-12" data-testid={`quiz-question-${step}`}>
            <div className="serif text-2xl md:text-4xl font-light leading-snug mb-10">{questions[step].text}</div>
            <div className="grid grid-cols-5 gap-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => choose(questions[step].id, n)}
                  className={`aspect-square rounded-xl border transition flex items-center justify-center serif text-2xl ${
                    answers[questions[step].id] === n
                      ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]"
                      : "border-white/10 hover:border-white/30 text-white/80 hover:bg-white/[0.04]"
                  }`}
                  data-testid={`quiz-answer-${step}-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-white/45 mt-3">
              <span>{labels[0]}</span>
              <span>{labels[4]}</span>
            </div>
            <div className="mt-8 flex justify-between">
              <button onClick={() => setStep(Math.max(0, step - 1))} className="btn-ghost text-sm flex items-center gap-2" data-testid="quiz-prev"><ArrowLeft size={14} /> Zurück</button>
              {answers[questions[step].id] && (
                <button onClick={() => setStep(step + 1)} className="btn-secondary text-sm" data-testid="quiz-next">Weiter <ArrowRight size={14} /></button>
              )}
            </div>
          </div>
        )}

        {step === total && (
          <form onSubmit={onSubmit} className="glass rounded-2xl p-8 md:p-12 space-y-4" data-testid="quiz-email-step">
            <h2 className="serif text-3xl">{t("quiz.email_step_title")}</h2>
            <p className="text-white/65">{t("quiz.email_step_sub")}</p>
            <input className="sat-input" placeholder={t("contact.first_name")} value={first} onChange={(e) => setFirst(e.target.value)} data-testid="quiz-first" />
            <input required type="email" className="sat-input" placeholder={t("contact.email")} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="quiz-email" />
            {/* Block 1 (Mastermatrix v1.1) · optionaler Newsletter — nicht vorangekreuzt */}
            <label className="flex items-start gap-3 text-xs text-white/60 leading-relaxed pt-1" data-testid="quiz-newsletter-label">
              <input
                type="checkbox"
                checked={newsletterOptIn}
                onChange={(e) => setNewsletterOptIn(e.target.checked)}
                className="mt-0.5 accent-[#C7A45B]"
                data-testid="quiz-newsletter-opt-in"
              />
              <span>
                {lang === "en"
                  ? "Optional: send me occasional letters from Nenad and SAT. I can unsubscribe at any time."
                  : "Optional: Schicke mir gelegentlich Briefe von Nenad und SAT. Ich kann mich jederzeit abmelden."}
              </span>
            </label>
            <button disabled={loading} type="submit" className="btn-primary w-full justify-center" data-testid="quiz-submit">
              {loading ? <Loader2 className="animate-spin" size={18} /> : (<>{t("quiz.submit")} <ArrowRight size={18} /></>)}
            </button>
            {err && <p className="text-red-300 text-xs">{err}</p>}
          </form>
        )}

        {step === total + 1 && result && (
          <div className="glass rounded-2xl p-8 md:p-12" data-testid="quiz-result">
            <div className="overline mb-3">{t("quiz.result_kicker")}</div>
            <h2 className="headline-xl text-4xl md:text-6xl headline-italic">{result.weakest_info.name}</h2>
            <p className="text-white/75 text-lg mt-6 leading-relaxed">{result.weakest_info.description}</p>
            <div className="mt-8 space-y-3" data-testid="quiz-scores">
              {Object.entries(result.scores).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-white/75">{result.rules_meta[k]?.name || k}</span>
                    <span className="text-[#D4AF37]">{v}/100</span>
                  </div>
                  <div className="h-px bg-white/10 relative overflow-hidden">
                    <div className="absolute left-0 top-0 h-px bg-gradient-to-r from-[#D4AF37] to-[#F3E5AB]" style={{ width: `${v}%` }}></div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/#kontakt" className="btn-primary" data-testid="quiz-result-cta">{t("quiz.result_cta")} <ArrowRight size={18} /></Link>
              <button onClick={restart} className="btn-secondary" data-testid="quiz-restart"><RefreshCcw size={14} /> {t("quiz.another")}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quiz;
